import React, { Suspense, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Preload } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import styled, { keyframes } from "styled-components";

// Keyframe animations for the loading skeleton
const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(133, 76, 230, 0.3), 
                0 0 40px rgba(133, 76, 230, 0.2),
                inset 0 0 30px rgba(133, 76, 230, 0.1);
  }
  50% {
    box-shadow: 0 0 30px rgba(133, 76, 230, 0.5), 
                0 0 60px rgba(133, 76, 230, 0.3),
                inset 0 0 40px rgba(133, 76, 230, 0.2);
  }
`;

// Container for the loading skeleton
const LoadingContainer = styled.div`
  width: 100%;
  height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;

  @media (max-width: 960px) {
    height: 300px;
  }
`;

// Circular skeleton representing the Earth
const SkeletonOrb = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(133, 76, 230, 0.1) 0%,
    rgba(133, 76, 230, 0.05) 50%,
    rgba(0, 70, 209, 0.1) 100%
  );
  animation: ${pulse} 2s ease-in-out infinite, ${glow} 3s ease-in-out infinite;
  position: relative;

  @media (max-width: 960px) {
    width: 150px;
    height: 150px;
  }

  &::before {
    content: "";
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    border: 2px solid transparent;
    border-top-color: rgba(133, 76, 230, 0.6);
    border-right-color: rgba(133, 76, 230, 0.3);
    animation: ${rotate} 1.5s linear infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    border-radius: 50%;
    border: 1px dashed rgba(133, 76, 230, 0.2);
    animation: ${rotate} 4s linear infinite reverse;
  }
`;

// Loading text
const LoadingText = styled.span`
  color: rgba(133, 76, 230, 0.7);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  animation: ${pulse} 2s ease-in-out infinite;
`;

// The loading skeleton component
const EarthLoadingSkeleton = () => (
  <LoadingContainer>
    <SkeletonOrb />
    <LoadingText>Loading 3D Model...</LoadingText>
  </LoadingContainer>
);

const Earth = () => {
  const [model, setModel] = useState(null);

  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load(
      "/scene.gltf",
      (gltf) => {
        setModel(gltf.scene);
      },
      undefined,
      (error) => {
        console.error("Failed to load 3D model:", error);
      }
    );
  }, []);

  if (!model) return null;

  return <primitive object={model} scale={3} position={[0, 0, 0]} rotation={[0, 0, 0]} />;
};

const EarthCanvas = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [modelLoaded, setModelLoaded] = useState(false);

  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load(
      "/scene.gltf",
      () => {
        setModelLoaded(true);
        // Small delay to ensure smooth transition
        setTimeout(() => setIsLoading(false), 300);
      },
      undefined,
      (error) => {
        console.error("Failed to load 3D model:", error);
        setIsLoading(false);
      }
    );
  }, []);

  if (isLoading && !modelLoaded) {
    return <EarthLoadingSkeleton />;
  }

  return (
    <Canvas
      shadows
      frameloop="demand"
      dpr={[1, 1.5]}
      gl={{ preserveDrawingBuffer: true, powerPreference: "high-performance" }}
      camera={{
        fov: 45,
        near: 0.1,
        far: 200,
        position: [-4, 3, 6],
      }}
    >
      <Suspense fallback={null}>
        <OrbitControls
          autoRotate
          enableZoom={false}
          maxPolarAngle={Math.PI / 2}
          minPolarAngle={Math.PI / 2}
        />
        <Earth />
        <Preload all />
      </Suspense>
    </Canvas>
  );
};

export default EarthCanvas;
